import { useEffect, useRef, useState } from 'react'
import NetworkStatusService from '@/shared/services/NetworkStatusService'

/**
 * Tracks network connectivity using NetworkStatusService.
 * Automatically verifies connection when the page regains focus
 * and periodically while offline.
 */
export default function useNetworkStatus(onChange?: (online: boolean) => void) {
  const service = NetworkStatusService.getInstance()
  const [isOnline, setIsOnline] = useState(service.getStatus())
  const attempts = useRef(0)
  const intervalRef = useRef<NodeJS.Timeout>()

  const checkConnection = async () => {
    const status = await service.getLatestStatusConnection()
    setIsOnline(status)
    if (onChange) onChange(status)
    return status
  }

  const scheduleCheck = () => {
    if (intervalRef.current) clearTimeout(intervalRef.current)
    const delay = Math.min(5000 * (attempts.current + 1), 60000)
    intervalRef.current = setTimeout(async () => {
      attempts.current += 1
      const status = await checkConnection()
      if (!status) scheduleCheck()
    }, delay)
  }

  useEffect(() => {
    const handleChange = (online: boolean) => {
      setIsOnline(online)
      if (onChange) onChange(online)
      if (!online) {
        attempts.current = 0
        scheduleCheck()
      } else if (intervalRef.current) {
        clearTimeout(intervalRef.current)
        intervalRef.current = undefined
      }
    }

    service.addListener(handleChange)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkConnection()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      service.removeListener(handleChange)
      document.removeEventListener('visibilitychange', handleVisibility)
      if (intervalRef.current) clearTimeout(intervalRef.current)
    }
  }, [])

  return isOnline
}
